import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp, Eye, FolderInput, Plus, RefreshCw } from 'lucide-react'
import {
  useCollectionRequestMutations,
  useCollectionRequests,
  useCollectionSet,
} from '../../../hooks'
import type {
  CollectionRequest,
  CreateCollectionRequestInput,
  StatusCountResponse,
  UpdateCollectionRequestInput,
} from '../../../types/case-container'
import { PackageReturnStatusBadges } from '../../../components/collections/collection-request-status-badge/PackageReturnStatusBadges'
import { CollectionRequestModals, type ModalState } from './CollectionRequestModals'
import { ExpandedRequestDetail } from './ExpandedRequestDetail'
import { StatusSummaryGrid } from './RequestsStatusSummary'
import { statusColourFor, statusIconFor } from './request-status-styles'

interface CollectionRequestsViewProps {
  caseId: string
  expandRequestId: string | null
  onExpandHandled: () => void
}

interface RequestsHeaderProps {
  requestCount: number
  collectionSetCount: number
  onShowCollectionSet: () => void
  onRefetch: () => void
  onNewCollection: () => void
}

function RequestsHeader(props: RequestsHeaderProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { requestCount, collectionSetCount } = props
  const { onShowCollectionSet, onRefetch, onNewCollection } = props
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-lime-100 dark:bg-lime-950/50">
          <FolderInput className="h-5 w-5 text-lime-600 dark:text-lime-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('collections.title')}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('collections.requestCount', { count: requestCount })}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {collectionSetCount > 0 && (
          <button
            onClick={onShowCollectionSet}
            className="inline-flex items-center gap-2 rounded-lg border border-[#4e5e82]/20 bg-[#4e5e82]/5 px-3 py-2 text-sm font-medium text-[#5c6e94] transition-colors hover:bg-lime-100 dark:border-[#5c6e94] dark:bg-[#8a9bba]/15 dark:text-lime-400 dark:hover:bg-[#8a9bba]/20"
            title={t('collections.viewCollectionSet')}
          >
            <Eye className="h-4 w-4" />
            <span>{t('collections.collectionSet')}</span>
            <span className="rounded-full bg-[#4e5e82] px-2 py-0.5 text-xs text-white">
              {collectionSetCount}
            </span>
          </button>
        )}
        <button
          onClick={onRefetch}
          className="rounded-lg border border-slate-300 p-2 text-slate-500 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
          title={t('actions.refresh')}
        >
          <RefreshCw className="h-4 w-4" />
        </button>
        <button
          onClick={onNewCollection}
          className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94]"
        >
          <Plus className="h-4 w-4" />
          {t('collections.newCollection')}
        </button>
      </div>
    </div>
  )
}

function RequestRowHeader({
  request,
  isExpanded,
  onToggleExpand,
}: {
  request: CollectionRequest
  isExpanded: boolean
  onToggleExpand: () => void
}): React.ReactElement {
  const { t } = useTranslation('cases')
  const colourClass = statusColourFor(request.packageStatus)
  return (
    <button
      onClick={onToggleExpand}
      className="w-full rounded-t-lg p-4 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${colourClass}`}>
            {statusIconFor(request.packageStatus)}
          </div>
          <div>
            <h3 className="font-medium text-slate-900 dark:text-slate-100">
              {request.requestName}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {request.collectorType} | {t('collections.endpointCount', { count: request.targetCount })}
            </p>
          </div>
        </div>
        <div className="items-centre flex gap-2">
          <PackageReturnStatusBadges
            packageStatus={request.packageStatus}
            returnStatus={request.returnStatus}
            stallReason={request.stallReason}
            size="sm"
          />
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )}
        </div>
      </div>
    </button>
  )
}

interface RequestRowProps {
  caseId: string
  request: CollectionRequest
  isExpanded: boolean
  onToggleExpand: () => void
  onShowPackageBuild: () => void
  onShowScheduleImport: () => void
  onShowUpload: () => void
  onEdit: () => void
  onRefetch: () => void
}

function RequestRow(props: RequestRowProps): React.ReactElement {
  const { caseId, request, isExpanded, onToggleExpand } = props
  const { onShowPackageBuild, onShowScheduleImport, onShowUpload, onEdit, onRefetch } = props
  return (
    <div className="rounded-lg border border-slate-200 bg-white transition-colors dark:border-slate-700 dark:bg-slate-900">
      <RequestRowHeader request={request} isExpanded={isExpanded} onToggleExpand={onToggleExpand} />
      {isExpanded && (
        <ExpandedRequestDetail
          caseId={caseId}
          requestId={request.id}
          packageStatus={request.packageStatus}
          returnStatus={request.returnStatus}
          stallReason={request.stallReason}
          isLocked={request.isLocked}
          onShowPackageBuild={onShowPackageBuild}
          onShowScheduleImport={onShowScheduleImport}
          onShowUpload={onShowUpload}
          onEdit={onEdit}
          onRefetch={onRefetch}
        />
      )}
    </div>
  )
}

function RequestsEmptyState({ onCreateFirst }: { onCreateFirst: () => void }): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center dark:border-slate-700">
      <FolderInput className="mx-auto h-12 w-12 text-slate-300 dark:text-slate-600" />
      <p className="mt-4 text-slate-600 dark:text-slate-400">{t('collections.noRequestsYet')}</p>
      <button
        onClick={onCreateFirst}
        className="mt-4 text-sm text-lime-600 hover:underline dark:text-lime-400"
      >
        {t('collections.createFirstRequest')}
      </button>
    </div>
  )
}

interface RequestsListProps {
  caseId: string
  requests: CollectionRequest[]
  expandedRequestId: string | null
  modal: ModalState
  setExpandedRequestId: (id: string | null) => void
  refetchRequests: () => void
}

function RequestsList(props: RequestsListProps): React.ReactElement {
  const { caseId, requests, expandedRequestId, setExpandedRequestId, modal, refetchRequests } =
    props
  if (requests.length === 0) {
    return (
      <RequestsEmptyState
        onCreateFirst={() => {
          modal.setShowCreateModal(true)
        }}
      />
    )
  }
  return (
    <div className="space-y-3">
      {requests.map((request) => (
        <RequestRow
          key={request.id}
          caseId={caseId}
          request={request}
          isExpanded={expandedRequestId === request.id}
          onToggleExpand={() => {
            setExpandedRequestId(expandedRequestId === request.id ? null : request.id)
          }}
          onShowPackageBuild={() => {
            modal.setShowPackageBuildFor(request.id)
          }}
          onShowScheduleImport={() => {
            modal.setShowScheduleImportFor(request.id)
          }}
          onShowUpload={() => {
            modal.setShowUploadModal(request.id)
          }}
          onEdit={() => {
            modal.setShowEditFor(request.id)
          }}
          onRefetch={refetchRequests}
        />
      ))}
    </div>
  )
}

function LoadingState(): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <RefreshCw className="h-6 w-6 animate-spin text-[#4e5e82]" />
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('collections.loadingCollections')}
        </p>
      </div>
    </div>
  )
}

function ErrorState({
  error,
  onRetry,
}: {
  error: string
  onRetry: () => void
}): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="text-center">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <button
          onClick={onRetry}
          className="mt-4 text-sm text-lime-600 hover:underline dark:text-lime-400"
        >
          {t('actions.tryAgain')}
        </button>
      </div>
    </div>
  )
}

function useModalState(): ModalState {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showCollectionSetModal, setShowCollectionSetModal] = useState(false)
  const [showPackageBuildFor, setShowPackageBuildFor] = useState<string | null>(null)
  const [showScheduleImportFor, setShowScheduleImportFor] = useState<string | null>(null)
  const [showEditFor, setShowEditFor] = useState<string | null>(null)
  const [showUploadModal, setShowUploadModal] = useState<string | null>(null)
  return {
    showCreateModal,
    showCollectionSetModal,
    showPackageBuildFor,
    showScheduleImportFor,
    showEditFor,
    showUploadModal,
    setShowCreateModal,
    setShowCollectionSetModal,
    setShowPackageBuildFor,
    setShowScheduleImportFor,
    setShowEditFor,
    setShowUploadModal,
  }
}

interface RequestsState {
  expandedRequestId: string | null
  setExpandedRequestId: (id: string | null) => void
  modal: ModalState
  requests: CollectionRequest[]
  statusCounts: StatusCountResponse
  loading: boolean
  error: string | null
  collectionSetCount: number
  refetchRequests: () => void
  handleCreate: (data: CreateCollectionRequestInput) => Promise<void>
  handleUpdate: (id: string, input: UpdateCollectionRequestInput) => Promise<void>
  creating: boolean
  updating: boolean
  mutationError: string | null
}

function useRequestsState(props: CollectionRequestsViewProps): RequestsState {
  const { caseId, expandRequestId, onExpandHandled } = props
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(expandRequestId)
  const modal = useModalState()
  const { requests, statusCounts, loading, error, refetch } = useCollectionRequests(caseId)
  const mutations = useCollectionRequestMutations()
  const { count: collectionSetCount } = useCollectionSet()

  useEffect(() => {
    if (expandRequestId && expandedRequestId !== expandRequestId) {
      setExpandedRequestId(expandRequestId)
      onExpandHandled()
    }
  }, [expandRequestId, expandedRequestId, onExpandHandled])

  const refetchRequests = (): void => {
    void refetch()
  }
  const handleCreate = async (data: CreateCollectionRequestInput): Promise<void> => {
    const result = await mutations.create(caseId, data)
    if (result) {
      modal.setShowCreateModal(false)
      void refetch()
    }
  }
  const handleUpdate = async (id: string, input: UpdateCollectionRequestInput): Promise<void> => {
    const result = await mutations.update(caseId, id, input)
    if (result) {
      modal.setShowEditFor(null)
      void refetch()
    }
  }
  return {
    expandedRequestId,
    setExpandedRequestId,
    modal,
    requests,
    statusCounts,
    loading,
    error,
    collectionSetCount,
    refetchRequests,
    handleCreate,
    handleUpdate,
    creating: mutations.creating,
    updating: mutations.updating,
    mutationError: mutations.error,
  }
}

export function CollectionRequestsView(props: CollectionRequestsViewProps): React.ReactElement {
  const state = useRequestsState(props)
  const { caseId } = props
  if (state.loading) return <LoadingState />
  if (state.error) return <ErrorState error={state.error} onRetry={state.refetchRequests} />
  return (
    <div className="space-y-6">
      <RequestsHeader
        requestCount={state.requests.length}
        collectionSetCount={state.collectionSetCount}
        onShowCollectionSet={() => {
          state.modal.setShowCollectionSetModal(true)
        }}
        onRefetch={state.refetchRequests}
        onNewCollection={() => {
          state.modal.setShowCreateModal(true)
        }}
      />
      <StatusSummaryGrid statusCounts={state.statusCounts} />
      <RequestsList
        caseId={caseId}
        requests={state.requests}
        expandedRequestId={state.expandedRequestId}
        setExpandedRequestId={state.setExpandedRequestId}
        modal={state.modal}
        refetchRequests={state.refetchRequests}
      />
      <CollectionRequestModals
        caseId={caseId}
        modal={state.modal}
        requests={state.requests}
        onCreate={state.handleCreate}
        onUpdate={state.handleUpdate}
        creating={state.creating}
        updating={state.updating}
        mutationError={state.mutationError}
        refetchRequests={state.refetchRequests}
      />
    </div>
  )
}
